import {WriteButton} from '../../common/buttons/WriteActions';
import {
    Box,
    Alert,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    Snackbar, Typography
} from '@mui/material';
import {ArrowBackIosNew, Delete, Edit} from '@mui/icons-material';
import {useNavigate, useParams} from 'react-router-dom';
import {customerAPI} from '../../../services/CustomerServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useEffect, useState} from 'react';
import {DeleteCustomerDialog} from './DeleteCustomerDialog';
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';

const ContactValue = ({label, value, href}: {label: string; value?: string | null; href?: string}) => value ? (
    <Box>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="body1">
            {href ? <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{value}</a> : value}
        </Typography>
    </Box>
) : null;

export const CustomerDetailCard = () => {
    const navigate = useNavigate();
    const {id} = useParams();
    const {t} = useAppTranslation();
    const {data: customer, isLoading, error: loadError} = customerAPI.useGetCustomerByIdV2Query(id ?? '', {skip: !id});
    const [deleteCustomer, {isLoading: isDeleting}] = customerAPI.useDeleteCustomerV2Mutation();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');
    const composedName = customer ? [customer.firstName, customer.lastName].filter(Boolean).join(' ') : '';
    const customerName = customer ? customer.name || composedName || customer.email || String(customer.id) : '';
    const fullName = composedName || customer?.name;

    useEffect(() => {
        if (!loadError || getApiErrorKind(loadError) !== 'server') return;
        setServerToast(getApiErrorMessage(loadError, t));
        setTraceId(getApiTraceId(loadError));
    }, [loadError, t]);

    const handleDelete = async () => {
        if (!customer?.id) return;
        setDeleteError('');
        try {
            await deleteCustomer(customer.id).unwrap();
            setDeleteDialogOpen(false);
            navigate('..', {relative: 'path'});
        } catch (error) {
            if (getApiErrorKind(error) === 'server') {
                setServerToast(getApiErrorMessage(error, t));
                setTraceId(getApiTraceId(error));
            } else {
                setDeleteError(getApiErrorMessage(error, t));
            }
        }
    };

    return (
        <Box sx={{width: '100%'}}>
            <Button variant="outlined" startIcon={<ArrowBackIosNew/>} onClick={() => navigate('..', {relative: 'path'})}>
                {t('customers:back_to_customers')}
            </Button>
            {isLoading && <Box sx={{mt: 2}}><ContentSkeleton variant="details"/></Box>}
            {customer && (
                <Stack spacing={2} sx={{mt: 2}}>
                    <Card>
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="h5">{customerName || t('customers:customer_info')}</Typography>
                                <Divider/>
                                <Stack spacing={1}>
                                    {fullName && (
                                        <ContactValue label={t('customers:full_name')} value={fullName}/>
                                    )}
                                    <ContactValue label={t('customers:email')} value={customer.email} href={`mailto:${customer.email}`}/>
                                    <ContactValue label={t('customers:phone_number')} value={customer.phoneNumber} href={`tel:${customer.phoneNumber}`}/>
                                    <ContactValue label={t('customers:instagram')} value={customer.instagram} href={customer.instagram ? (customer.instagram.startsWith('http') ? customer.instagram : `https://${customer.instagram}`) : undefined}/>
                                    <ContactValue label={t('customers:facebook')} value={customer.faceBook} href={customer.faceBook ? (customer.faceBook.startsWith('http') ? customer.faceBook : `https://${customer.faceBook}`) : undefined}/>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <WriteButton variant="outlined" startIcon={<Edit/>} onClick={() => navigate('edit')}>
                            {t('common:edit')}
                        </WriteButton>
                        <WriteButton variant="contained" color="error" startIcon={<Delete/>} disabled={isDeleting} onClick={() => setDeleteDialogOpen(true)}>
                            {t('common:delete')}
                        </WriteButton>
                    </Stack>
                </Stack>
            )}
            {!isLoading && loadError && getApiErrorKind(loadError) !== 'server' && <Alert severity="error" sx={{mt: 2}}>{getApiErrorMessage(loadError, t)}</Alert>}
            {!isLoading && !customer && !loadError && <Alert severity="error" sx={{mt: 2}}>{t('common:no_records_found')}</Alert>}
            {customer && (
                <DeleteCustomerDialog
                    open={deleteDialogOpen}
                    customerName={customerName}
                    isDeleting={isDeleting}
                    onClose={() => setDeleteDialogOpen(false)}
                    onDelete={handleDelete}
                    error={deleteError}
                />
            )}
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Box>
    );
};
